import { useEffect } from "react";

import { CalendarHeader } from "~/screens/Scheduling/InitialVisit/components/CalendarHeader";
import { CalendarPopup } from "~/screens/Scheduling/InitialVisit/components/CalendarPopup";
import { DaySelector } from "~/screens/Scheduling/InitialVisit/components/DaySelector";
import { ProviderSchedulesSection } from "~/screens/Scheduling/InitialVisit/components/ProviderSchedulesSection";
import { useBookingFlowEvents } from "~/services/booking-flows/hooks/use-booking-flow-events";
import type { VisitType } from "~/services/scheduling/types";
import { INSURANCE_NOT_FOUND_OPTION } from "~/shared.types";
import { Button } from "~/shared/components/ui/Button/Button";
import type { AppointmentDetails } from "~/stores/use-patient-store";
import { icons } from "~/ui/common/Icons";
import { StaffBookingFooter } from "../components/staff-booking-footer";
import { StepHeading } from "../components/step-heading";
import { useStaffSlots } from "../hooks/use-staff-slots";

type SlotStepProps = {
  visitType: VisitType;
  stateId: number;
  insuranceId: number | null;
  providerId?: number | null;
  initialSelection?: AppointmentDetails | null;
  onContinue: (appointmentDetails: AppointmentDetails) => void;
  onBack: () => void;
};

export const SlotStep = ({
  visitType,
  stateId,
  insuranceId,
  providerId,
  initialSelection,
  onContinue,
  onBack,
}: SlotStepProps) => {
  const { trackSchedulingEntryPage, trackSelectedSlot } =
    useBookingFlowEvents();

  useEffect(() => {
    trackSchedulingEntryPage();
  }, []);

  const {
    calendar,
    selection,
    handlers,
    appointmentSlotsByProvider,
    isPending,
    isError,
    retry,
    hasNoAvailability,
    getAppointmentDetails,
  } = useStaffSlots({
    visitType,
    stateId,
    providerId,
    initialSelection,
    insuranceId:
      insuranceId === INSURANCE_NOT_FOUND_OPTION.value
        ? undefined
        : insuranceId,
  });

  const handleContinue = () => {
    const details = getAppointmentDetails();
    if (!details?.timeSlot) return;

    trackSelectedSlot(
      details.timeSlot,
      details.formattedDate,
      details.provider?.id,
    );
    onContinue(details);
  };

  return (
    <div className="flex min-h-full flex-col gap-4">
      <StepHeading title="Book a visit" visitType={visitType} />

      {isError ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-2 py-10 text-center"
        >
          <icons.outline.ExclamationCircleIcon
            aria-hidden="true"
            className="size-6 text-gray-700"
          />
          <p className="text-lg font-medium text-gray-700">
            Slots could not be loaded
          </p>
          <p className="text-sm text-gray-500">
            Something went wrong while loading availability.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={retry}
          >
            Try again
          </Button>
        </div>
      ) : hasNoAvailability ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <icons.outline.CalendarIcon
            aria-hidden="true"
            className="size-6 text-gray-700"
          />
          <p className="text-lg font-medium text-gray-700">
            No slots available
          </p>
          <p className="text-sm text-gray-500">
            {providerId
              ? "This provider has no availability in the coming months. Go back to choose another provider."
              : "No provider has availability for this state and insurance in the coming months. Go back to choose another state or insurance."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <CalendarHeader
            calendarDays={calendar.calendarDays}
            onDaySelect={handlers.onDaySelect}
            selectedDay={calendar.selectedDay}
            onMonthChange={handlers.onMonthChange}
            onOpen={handlers.onOpenCalendar}
            className="py-0"
          />
          <DaySelector
            days={calendar.calendarDays}
            selectedDay={calendar.selectedDay}
            onDaySelect={handlers.onDaySelect}
            hasAppointments={calendar.hasAppointments}
            onMonthChange={handlers.onMonthChange}
            currentMonthYear={calendar.monthYear}
            maxAllowedDate={calendar.maxAllowedDate}
            hideSelectedDay={!isPending && !appointmentSlotsByProvider.length}
          />
          <ProviderSchedulesSection
            appointmentSlotsByProvider={appointmentSlotsByProvider}
            selectedDate={calendar.selectedDay.date}
            selectedTimeSlot={selection.selectedTimeSlot}
            selectedProvider={selection.selectedProvider}
            onTimeSlotSelect={handlers.onTimeSlotSelect}
            isPending={isPending}
            scheduleOptions={{ variant: "compact" }}
            scrollable={false}
          />
        </div>
      )}

      <StaffBookingFooter
        onBack={onBack}
        continueProps={{
          type: "button",
          onClick: handleContinue,
          disabled: !selection.selectedTimeSlot,
        }}
        sticky={!!selection.selectedTimeSlot}
      />

      {calendar.isCalendarOpen && (
        <CalendarPopup
          onClose={handlers.onCloseCalendar}
          onDaySelect={handlers.onDaySelect}
          calendarDays={calendar.calendarDays}
          isSelectable={calendar.hasAppointments}
          selectedDay={calendar.selectedDay}
          onMonthChange={handlers.onMonthChange}
          initialDate={calendar.selectedDay.date}
          isPending={isPending}
        />
      )}
    </div>
  );
};
